import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import TopNav from '../components/TopNav';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Building2, TrendingUp, Rocket, Handshake, ArrowRight, Users, DollarSign } from 'lucide-react';

export default function HomePage() {
  return (
    <div className="min-h-screen flex flex-col">
      <TopNav />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative overflow-hidden noise-overlay bg-gradient-to-b from-muted/50 to-background">
          <div className="absolute inset-0 gradient-steel opacity-20 pointer-events-none" />
          
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Building the Future
                <br />
                <span style={{ color: 'var(--ap-blue)' }}>Through Automation</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-10">
                Building the future through automation. We create, launch, grow, partner with, and acquire companies that simplify how businesses operate.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/portfolio">
                  <Button size="lg" className="rounded-xl shadow-sm hover:shadow-md transition-shadow">
                    View Portfolio <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="rounded-xl border-2">
                    Partner With Us
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
        
        {/* What We Do Section */}
        <section className="py-20 bg-background">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-semibold tracking-tight mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>How We Build Value</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                We don't just invest—we actively transform businesses into automation-first operations.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="rounded-xl border border-border/70 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <Rocket className="h-12 w-12 mb-4" style={{ color: 'var(--ap-blue)' }} />
                  <CardTitle className="text-lg">Create & Launch</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">Build new ventures from the ground up with automation embedded from day one.</p>
                </CardContent>
              </Card>
              
              <Card className="rounded-xl border border-border/70 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <TrendingUp className="h-12 w-12 mb-4" style={{ color: 'var(--ap-blue)' }} />
                  <CardTitle className="text-lg">Scale & Grow</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">Accelerate portfolio companies through proven automation frameworks and systems.</p>
                </CardContent>
              </Card>
              
              <Card className="rounded-xl border border-border/70 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <Handshake className="h-12 w-12 mb-4" style={{ color: 'var(--ap-blue)' }} />
                  <CardTitle className="text-lg">Partner</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">Collaborate with founders who share our vision for automation-driven growth.</p>
                </CardContent>
              </Card>
              
              <Card className="rounded-xl border border-border/70 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <Building2 className="h-12 w-12 mb-4" style={{ color: 'var(--ap-blue)' }} />
                  <CardTitle className="text-lg">Acquire</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">Strategic acquisitions of companies with strong fundamentals ready for transformation.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
        
        {/* CTA Section */}
        <section className="py-20 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Ready to Build Together?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Whether you're a founder looking for the right partner or an investor seeking opportunities in automation-first businesses, we'd love to hear from you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/automation-fund">
                <Button size="lg" variant="outline" className="rounded-xl border-2">
                  Explore Automation Fund
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" className="rounded-xl">
                  Get in Touch
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
